"use client";

import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { IPages, ITheme, IUserData } from "@/types";
import UserImage from "./heading/sections/UserImage";
import { RevalidateLink } from "../layout/RevalidateLink";
import { Button } from "@/components/ui/button";
import { IconHome, IconHomeFilled, IconMenu2 } from "@tabler/icons-react";
import { cn, getTextColorForBackground, rgbToHex } from "@/lib/utils";
import { usePathname } from "next/navigation";
import React from "react";
import ProfileShare from "./ProfileShare";

interface Props {
    user: IUserData;
    admin: boolean;
    isHeadingInView: boolean;
    theme: ITheme;
    pages: IPages[];
    domain?: Boolean;
    impersonate?: boolean;
    blogPage?: boolean;
}

export default function BottomNav({
    user,
    impersonate,
    admin,
    isHeadingInView,
    theme,
    pages,
    domain,
    blogPage = false,
}: Props) {
    // Admin
    const textColor = theme.accent_col
        ? rgbToHex(theme.accent_col)
        : theme.text_col_button
          ? theme.text_col_button
          : theme.button;
    const backgroundColor = getTextColorForBackground(
        theme.accent_col
            ? rgbToHex(theme.accent_col)
            : theme.text_col_button
              ? theme.text_col_button
              : theme.button
    );
    // End Admin
    const path = usePathname();
    const pagemap = pages
        .filter((e) => e.enabled)
        .sort((a, b) => a.sort - b.sort);
    const isRoot = path === "/";
    const isCustomDomain =
        typeof window !== "undefined" &&
        window.location.hostname !== "doras.to";
    // Helper for Home button style
    function getHomeButtonStyle({
        path,
        user,
        theme,
        isCustomDomain,
        backgroundColor,
    }: {
        path: string;
        user: IUserData;
        theme: ITheme;
        isCustomDomain: boolean;
        backgroundColor: string;
    }) {
        // Home is active if:
        // - custom domain and path === "/"
        // - main domain and path === `/${user.username}`
        // Fix: Also treat /[username] as active for Home on main domain
        const isActive =
            (isCustomDomain && path === "/") ||
            (!isCustomDomain && (path === `/${user.username}` || path === "/"));
        let background = isActive ? theme.button : "transparent";
        let color: string;
        if (isActive) {
            if (
                theme.text_col_button &&
                theme.text_col_button !== theme.button
            ) {
                color = theme.text_col_button;
            } else {
                color = getTextColorForBackground(rgbToHex(theme.button));
            }
        } else {
            color =
                backgroundColor ||
                getTextColorForBackground(rgbToHex(theme.button));
        }
        return {
            background,
            color,
            underline: isActive,
            iconFilled: isActive,
        };
    }

    // Helper for page button style
    function getPageButtonStyle({
        path,
        pageSlug,
        user,
        theme,
        isCustomDomain,
        backgroundColor,
    }: {
        path: string;
        pageSlug: string;
        user: IUserData;
        theme: ITheme;
        isCustomDomain: boolean;
        backgroundColor: string;
    }) {
        // Page is active if:
        // - path ends with /pageSlug
        // - and not on the home page (custom domain: path !== "/", main domain: path !== `/${user.username}`)
        const isHome =
            (isCustomDomain && path === "/") ||
            (!isCustomDomain && path === `/${user.username}`);
        const isActive = path.endsWith(`/${pageSlug}`) && !isHome;
        let background = isActive ? theme.button : "transparent";
        let color: string;
        if (isActive) {
            if (
                theme.text_col_button &&
                theme.text_col_button !== theme.button
            ) {
                color = theme.text_col_button;
            } else {
                color = getTextColorForBackground(rgbToHex(theme.button));
            }
        } else {
            color =
                backgroundColor ||
                getTextColorForBackground(rgbToHex(theme.button));
        }
        return {
            background,
            color,
        };
    }

    return (
        <div
            // className={`sticky bottom-1 z-50 flex max-w-2xl items-center pr-2 ${theme.button_rounded} ${pages.length >= 1 && blogPage == false ? "  mx-auto w-full" : "hidden"} ${blogPage == true && "mx-auto flex w-full"}`}
            className={cn(
                "z-50 flex max-w-2xl items-center pr-2",
                pages.length >= 1 ? "mx-auto w-full" : "hidden",
                blogPage == true
                    ? "mx-auto mb-1 flex w-full"
                    : "sticky bottom-1"
            )}
            style={{
                background:
                    pages.length < 1
                        ? "transparent"
                        : theme.accent_col || theme.button,
            }}
        >
            {pages.length >= 1 && (
                <div className="noscroll flex w-full items-center gap-1 overflow-x-auto py-1">
                    <RevalidateLink
                        href={
                            admin
                                ? `/admin/content`
                                : domain
                                  ? `/`
                                  : `/${user.username}`
                        }
                        className="group"
                    >
                        {(() => {
                            const homeBtn = getHomeButtonStyle({
                                path,
                                user,
                                theme,
                                isCustomDomain,
                                backgroundColor,
                            });
                            return (
                                <Button
                                    className={cn(
                                        `${theme.button_rounded} shadow-none transition-all duration-100 group-hover:scale-110`,
                                        homeBtn.underline ? "underline" : ""
                                    )}
                                    style={{
                                        background: homeBtn.background,
                                        color: homeBtn.color,
                                    }}
                                >
                                    {homeBtn.iconFilled ? (
                                        <IconHomeFilled />
                                    ) : (
                                        <IconHome />
                                    )}
                                </Button>
                            );
                        })()}
                    </RevalidateLink>
                    {pagemap.slice(0, 3).map((page, index) => (
                        <RevalidateLink
                            key={index}
                            href={
                                admin
                                    ? `/admin/content/${page.slug}`
                                    : domain
                                      ? `/${page.slug}`
                                      : `/${user.username}/${page.slug}`
                            }
                            className="group z-[999]"
                        >
                            {(() => {
                                const pageBtn = getPageButtonStyle({
                                    path,
                                    pageSlug: page.slug,
                                    user,
                                    theme,
                                    isCustomDomain,
                                    backgroundColor,
                                });
                                return (
                                    <Button
                                        className={`${theme.button_rounded} font-bold shadow-none transition-all duration-100 group-hover:scale-105`}
                                        style={{
                                            background: pageBtn.background,
                                            color: pageBtn.color,
                                        }}
                                    >
                                        {page.name}
                                    </Button>
                                );
                            })()}
                        </RevalidateLink>
                    ))}
                </div>
            )}
            <DropdownMenu>
                <DropdownMenuTrigger asChild>
                    <Button
                        size="icon"
                        className={`ml-auto h-auto w-auto p-1 transition-all duration-100 hover:scale-105 ${theme.button_rounded}`}
                        style={{
                            background: backgroundColor,
                            color: textColor,
                        }}
                    >
                        <IconMenu2 size={24} />
                    </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                    className="min-w-60 max-w-96 border-0 text-xl shadow-xl"
                    style={{
                        background: theme.accent_col || theme.button,
                        color: backgroundColor,
                    }}
                >
                    <DropdownMenuLabel className="flex flex-col items-start justify-start gap-1 text-xl">
                        <div className="flex items-center gap-1">
                            <UserImage user={user} className="size-10" />
                            <p className="truncate">{user.displayname}</p>
                        </div>
                        <DropdownMenuItem asChild>
                            <ProfileShare
                                theme={theme}
                                user={user}
                                admin={admin}
                            />
                        </DropdownMenuItem>
                    </DropdownMenuLabel>
                    <DropdownMenuSeparator />
                    <DropdownMenuLabel
                        className={cn(
                            "justify-normal gap-1",
                            pages.length == 0 && "hidden"
                        )}
                    >
                        {pages.length == 0 ? null : (
                            <p className="truncate">My Pages</p>
                        )}
                        {/* <p className="truncate">My Pages</p> */}
                    </DropdownMenuLabel>

                    {/* Map of pages */}
                    <div className="max-h-48 w-full overflow-auto">
                        <RevalidateLink
                            href={domain ? `/` : `/${user.username}`}
                            className={`flex px-2 py-1 text-lg hover:underline`}
                        >
                            <p className="truncate">Home</p>
                        </RevalidateLink>
                        {pagemap.map((page, index) => (
                            <RevalidateLink
                                key={index}
                                href={
                                    admin
                                        ? `/admin/content/${page.slug}`
                                        : domain
                                          ? `/${page.slug}`
                                          : `/${user.username}/${page.slug}`
                                }
                                className={`flex px-2 py-1 text-lg hover:underline`}
                            >
                                <p className="truncate">{page.name}</p>
                            </RevalidateLink>
                        ))}
                        <div className="!visible rounded-md !bg-card !text-foreground">
                            <RevalidateLink
                                href={`/report?user=${user.id}`}
                                className={`flex px-2 py-1 text-lg hover:underline`}
                            >
                                <p className="truncate text-sm">
                                    Report this profile
                                </p>
                            </RevalidateLink>
                        </div>
                    </div>
                    {user.settings.branding == false && (
                        <>
                            <DropdownMenuSeparator />
                            <div className="flex flex-col items-center rounded-md bg-primary p-2">
                                <a
                                    href={
                                        user.affiliate == null
                                            ? "/"
                                            : "/?aff=" + user.affiliate
                                    }
                                    target="_blank"
                                >
                                    <Button className="gap-3 font-bold">
                                        <img
                                            src="https://cdn.doras.to/doras/icon-white.svg"
                                            className="h-6 w-6"
                                            alt="Doras Logo"
                                        />
                                        Create your profile
                                    </Button>
                                </a>
                            </div>
                        </>
                    )}
                </DropdownMenuContent>
            </DropdownMenu>

            {/* Add any additional navigation items */}
        </div>
    );
}
